import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Bot, Loader2, Trash2, Sparkles, Target, MessageSquare,
  FileQuestion, FileBox, FileText, Eye, Save, Trash, Network,
} from "lucide-react";

const AGENT_TYPE_LABELS = {
  receptionist: "Receptionist",
  sales: "Sales",
  support: "Support",
  booking: "Booking",
  custom: "Custom",
};
const AGENT_TYPE_ACCENT = {
  receptionist: "bg-sky-100 text-sky-800 border-sky-200",
  sales: "bg-emerald-100 text-emerald-800 border-emerald-200",
  support: "bg-amber-100 text-amber-800 border-amber-200",
  booking: "bg-purple-100 text-purple-800 border-purple-200",
  custom: "bg-zinc-100 text-zinc-800 border-zinc-200",
};

/**
 * Stand-alone editor for a single AI phone agent. Replaces the
 * previous in-list dialog so admins get a full-screen workspace
 * for the answer script, persona, objectives, question playbook,
 * closing script, hand-off rules, and reference-doc library.
 */
export default function AiPhoneAgentEditorPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", agent_type: "receptionist", description: "", is_active: true,
    answer_script: "", persona: "", objectives: "", questions: [],
    closing_script: "", handoff_rules: "", handoff_sub_agent_ids: [],
  });
  const [agent, setAgent] = useState(null);
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questionDraft, setQuestionDraft] = useState("");
  const [dirty, setDirty] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [one, list] = await Promise.all([
        api.get(`/ai-phone-agents/${agentId}`),
        api.get("/ai-phone-agents"),
      ]);
      const data = one.data;
      setAgent(data);
      setAllAgents(list.data?.agents || []);
      setForm({
        name: data.name || "",
        agent_type: data.agent_type || "receptionist",
        description: data.description || "",
        is_active: data.is_active ?? true,
        answer_script: data.answer_script || "",
        persona: data.persona || "",
        objectives: data.objectives || "",
        questions: data.questions || [],
        closing_script: data.closing_script || "",
        handoff_rules: data.handoff_rules || "",
        handoff_sub_agent_ids: data.handoff_sub_agent_ids || [],
      });
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Agent not found");
      navigate("/ai-phone-agents");
    } finally { setLoading(false); }
  }, [agentId, navigate]);
  useEffect(() => { reload(); }, [reload]);

  const patch = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    setSaving(true);
    try {
      const { data } = await api.patch(`/ai-phone-agents/${agentId}`, form);
      toast.success("Agent saved");
      setAgent(data);
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.name}" and all its reference docs?`)) return;
    try {
      await api.delete(`/ai-phone-agents/${agentId}`);
      toast.success("Agent deleted");
      navigate("/ai-phone-agents");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const addQuestion = () => {
    const q = questionDraft.trim();
    if (!q) return;
    setForm((f) => ({ ...f, questions: [...(f.questions || []), q] }));
    setQuestionDraft("");
    setDirty(true);
  };
  const removeQuestion = (i) => {
    setForm((f) => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }));
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin inline" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl" data-testid="ai-phone-agent-editor-page">
      <Link to="/ai-phone-agents"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="ai-agent-back">
        <ArrowLeft className="h-3 w-3" /> Back to all agents
      </Link>

      {/* Sticky header with save button */}
      <div className="sticky top-0 bg-white/95 backdrop-blur z-10 -mx-6 px-6 py-3 mb-5 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center rounded-sm bg-purple-100 text-purple-700">
            <Bot className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline">AI · PHONE AGENT</div>
            <h1 className="font-display font-extrabold text-2xl tracking-tight truncate">
              {form.name || "Untitled agent"}
              {dirty && <span className="ml-2 text-amber-600 text-sm">· unsaved</span>}
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="outline" className={`rounded-sm text-[10px] ${AGENT_TYPE_ACCENT[form.agent_type] || AGENT_TYPE_ACCENT.custom}`}>
                {AGENT_TYPE_LABELS[form.agent_type] || form.agent_type}
              </Badge>
              {!form.is_active && (
                <Badge variant="outline" className="rounded-sm text-[10px] bg-zinc-100 text-zinc-600">Inactive</Badge>
              )}
              <span className="text-[10px] text-zinc-500 font-mono">{agent?.agent_id}</span>
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-sm text-zinc-600 mr-2">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => patch("is_active", e.target.checked)}
              data-testid="ai-agent-active" />
            Active
          </label>
          <Button variant="outline" onClick={remove}
            className="rounded-sm text-red-700 border-red-200 hover:bg-red-50"
            data-testid="ai-agent-delete">
            <Trash className="h-4 w-4 mr-1.5" /> Delete
          </Button>
          <Button onClick={save} disabled={saving || !dirty}
            className="rounded-sm bg-purple-700 hover:bg-purple-800"
            data-testid="ai-agent-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Identity */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <SectionLabel icon={Bot}>Name</SectionLabel>
                <Input value={form.name} onChange={(e) => patch("name", e.target.value)}
                  placeholder="e.g. IT Sales agent"
                  data-testid="ai-agent-name" className="rounded-sm" />
              </div>
              <div>
                <SectionLabel icon={Sparkles}>Type</SectionLabel>
                <Select value={form.agent_type}
                  onValueChange={(v) => patch("agent_type", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="ai-agent-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(AGENT_TYPE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <SectionLabel>Short description</SectionLabel>
              <Input value={form.description}
                onChange={(e) => patch("description", e.target.value)}
                placeholder="One-liner shown on the agent card." className="rounded-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Persona + Objectives */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <SectionLabel icon={Sparkles}>Persona</SectionLabel>
              <Textarea rows={6} value={form.persona}
                onChange={(e) => patch("persona", e.target.value)}
                placeholder="How the agent should sound — tone, accent, pace, energy, vocabulary."
                data-testid="ai-agent-persona" className="rounded-sm" />
            </div>
            <div>
              <SectionLabel icon={Target}>Core objectives</SectionLabel>
              <Textarea rows={6} value={form.objectives}
                onChange={(e) => patch("objectives", e.target.value)}
                placeholder="Goals on every call — qualify caller, route accurately, book demo, resolve issue, etc."
                data-testid="ai-agent-objectives" className="rounded-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Answer script */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <SectionLabel icon={MessageSquare}>Opening / answer script &amp; rules</SectionLabel>
            <Textarea rows={6} value={form.answer_script}
              onChange={(e) => patch("answer_script", e.target.value)}
              placeholder={"e.g.\n\"Good morning, you've reached <Company>. This is <Agent>. How can I help?\"\n\nNever promise prices. Never disclose internal extensions."}
              data-testid="ai-agent-answer-script" className="rounded-sm" />
          </CardContent>
        </Card>

        {/* Question playbook */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <SectionLabel icon={FileQuestion}>Question playbook</SectionLabel>
            <p className="text-[11px] text-zinc-500 -mt-1 mb-2">
              The bank of qualifying questions the agent can draw from when talking to the caller.
            </p>
            <div className="flex gap-2 mb-2">
              <Input value={questionDraft}
                onChange={(e) => setQuestionDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addQuestion(); } }}
                placeholder="Add a question and press Enter..."
                className="rounded-sm"
                data-testid="ai-agent-question-input" />
              <Button onClick={addQuestion} className="rounded-sm shrink-0"
                data-testid="ai-agent-question-add">Add</Button>
            </div>
            <div className="space-y-1">
              {form.questions.length === 0 ? (
                <div className="text-xs italic text-zinc-400 px-2 py-3 text-center border border-dashed border-zinc-200 rounded-sm">
                  No questions yet. Add as many as you like.
                </div>
              ) : form.questions.map((q, i) => (
                <div key={`q-${i}-${q.slice(0, 40)}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-sm border border-zinc-200 bg-zinc-50"
                  data-testid={`ai-agent-question-row-${i}`}>
                  <span className="text-zinc-400 font-mono text-[10px] w-5">{i + 1}.</span>
                  <span className="flex-1 text-sm">{q}</span>
                  <button onClick={() => removeQuestion(i)}
                    className="text-zinc-400 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Closing + handoff */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <SectionLabel icon={MessageSquare}>Final closing script</SectionLabel>
              <Textarea rows={5} value={form.closing_script}
                onChange={(e) => patch("closing_script", e.target.value)}
                placeholder='e.g. "Thanks for calling — someone from our team will be in touch within 24 hours."'
                data-testid="ai-agent-closing" className="rounded-sm" />
            </div>
            <div>
              <SectionLabel>Hand-off rules (plain language)</SectionLabel>
              <Textarea rows={5} value={form.handoff_rules}
                onChange={(e) => patch("handoff_rules", e.target.value)}
                placeholder={"If caller mentions 'pricing' → Sales agent.\nIf caller mentions 'broken' or 'help' → Support agent."}
                data-testid="ai-agent-handoff" className="rounded-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Sub-agent hand-off picker — shown on every agent */}
        <SubAgentHandoffCard
          allAgents={allAgents}
          currentAgentId={agentId}
          selectedIds={form.handoff_sub_agent_ids}
          onChange={(ids) => patch("handoff_sub_agent_ids", ids)} />

        {/* References */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <ReferenceManager agentId={agentId}
              initialRefs={agent?.references || []} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ───────────── Sub-agent hand-off picker (multi-select) ───────────── */
function SubAgentHandoffCard({ allAgents, currentAgentId, selectedIds, onChange }) {
  // Filter out the agent itself — an agent can't hand off to itself.
  const others = (allAgents || []).filter((a) => a.agent_id !== currentAgentId);
  const selectedSet = new Set(selectedIds || []);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ai-agent-handoff-subagents">
      <CardContent className="p-5">
        <SectionLabel icon={Network}>Hand off to other agents</SectionLabel>
        <p className="text-[11px] text-zinc-500 -mt-1 mb-3">
          Tick every agent this one is allowed to transfer the live call to. The LLM uses these alongside your
          plain-language hand-off rules above. A general receptionist will usually have most boxes ticked;
          specialist agents typically have none or just one.
        </p>
        {others.length === 0 ? (
          <div className="text-xs italic text-zinc-400 px-2 py-3 text-center border border-dashed border-zinc-200 rounded-sm">
            No other agents to hand off to yet. Create more agents from the list page.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
            data-testid="ai-agent-handoff-grid">
            {others.map((a) => {
              const checked = selectedSet.has(a.agent_id);
              return (
                <label key={a.agent_id}
                  className={`flex items-start gap-2 p-2 rounded-sm border cursor-pointer transition ${
                    checked ? "border-purple-400 bg-purple-50/60" : "border-zinc-200 bg-white hover:border-zinc-300"
                  }`}
                  data-testid={`ai-agent-handoff-option-${a.agent_id}`}>
                  <input type="checkbox"
                    checked={checked}
                    onChange={() => toggle(a.agent_id)}
                    className="mt-1"
                    data-testid={`ai-agent-handoff-checkbox-${a.agent_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-display font-bold text-sm truncate">{a.name}</span>
                      <Badge variant="outline" className={`rounded-sm text-[9px] ${
                        AGENT_TYPE_ACCENT[a.agent_type] || AGENT_TYPE_ACCENT.custom
                      }`}>
                        {AGENT_TYPE_LABELS[a.agent_type] || a.agent_type}
                      </Badge>
                      {!a.is_active && (
                        <Badge variant="outline" className="rounded-sm text-[9px] bg-zinc-100 text-zinc-500">
                          inactive
                        </Badge>
                      )}
                    </div>
                    {a.description && (
                      <div className="text-[10px] text-zinc-500 line-clamp-2 mt-0.5">{a.description}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
        <div className="mt-3 text-[10px] font-mono text-zinc-500">
          {selectedSet.size} of {others.length} selected
        </div>
      </CardContent>
    </Card>
  );
}


/* ───────────────────────── Reference doc manager ───────────────────────── */
function ReferenceManager({ agentId, initialRefs }) {
  const [refs, setRefs] = useState(initialRefs || []);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { setRefs(initialRefs || []); }, [initialRefs]);

  const reload = useCallback(async () => {
    const { data } = await api.get(`/ai-phone-agents/${agentId}`);
    setRefs(data.references || []);
  }, [agentId]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/ai-phone-agents/${agentId}/references`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name} — converted to markdown`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (refId) => {
    if (!window.confirm("Delete this reference doc?")) return;
    try {
      await api.delete(`/ai-phone-agents/${agentId}/references/${refId}`);
      toast.success("Reference removed");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="ai-agent-references">
      <SectionLabel icon={FileBox}>Reference material</SectionLabel>
      <p className="text-[11px] text-zinc-500 -mt-1 mb-2">
        Upload product sheets, FAQs, price lists, troubleshooting guides — any .md / .txt / .docx / .pdf / .xlsx / .csv. We auto-convert to markdown so the LLM can quote from it during calls.
      </p>
      <div className="flex items-center gap-2 mb-2">
        <input type="file" ref={fileRef} onChange={onFile}
          accept=".md,.markdown,.txt,.docx,.pdf,.xlsx,.csv"
          data-testid="ai-agent-ref-file"
          className="text-xs" />
        {uploading && <Loader2 className="h-4 w-4 animate-spin text-purple-700" />}
      </div>
      <div className="space-y-1">
        {refs.length === 0 ? (
          <div className="text-xs italic text-zinc-400 px-2 py-3 text-center border border-dashed border-zinc-200 rounded-sm">
            No references uploaded yet.
          </div>
        ) : refs.map((r) => (
          <div key={r.ref_id}
            className="flex items-center gap-2 px-2 py-1.5 rounded-sm border border-zinc-200 bg-zinc-50"
            data-testid={`ai-agent-ref-${r.ref_id}`}>
            <FileText className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <span className="flex-1 text-sm truncate">{r.label || r.filename}</span>
            <span className="text-[10px] font-mono text-zinc-500">
              {r.extension} · {formatBytes(r.byte_size)} · {r.markdown_chars} chars
            </span>
            <button onClick={() => setPreview(r.ref_id)}
              className="text-zinc-400 hover:text-purple-700" title="Preview converted markdown">
              <Eye className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(r.ref_id)}
              className="text-zinc-400 hover:text-red-600" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      {preview && (
        <ReferencePreviewDialog agentId={agentId} refId={preview}
          onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

function ReferencePreviewDialog({ agentId, refId, onClose }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [md, setMd] = useState("");

  useEffect(() => {
    api.get(`/ai-phone-agents/${agentId}/references/${refId}`)
      .then((r) => { setData(r.data); setMd(r.data.markdown || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"));
  }, [agentId, refId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/ai-phone-agents/${agentId}/references/${refId}`,
                      { markdown: md });
      toast.success("Markdown updated");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-xl">
            {data?.label || data?.filename || "Reference"}
          </DialogTitle>
        </DialogHeader>
        {!data ? <Loader2 className="h-6 w-6 animate-spin mx-auto my-12" /> : (
          <>
            <p className="text-[11px] text-zinc-500 -mt-2 mb-2">
              Edit the auto-converted markdown below — your changes will be what the LLM sees at call-time.
            </p>
            <Textarea rows={20} value={md} onChange={(e) => setMd(e.target.value)}
              className="font-mono text-xs" />
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200 mt-3">
              <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
              <Button onClick={save} disabled={saving}
                className="rounded-sm bg-purple-700 hover:bg-purple-800">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save markdown
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="label-overline text-[10px] flex items-center gap-1 mb-1">
      {Icon && <Icon className="h-3 w-3" />} {children}
    </div>
  );
}
function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
